import type { EvalDimRating } from '@/common/api/models/evalDimRating';
import type { FlatTaskEvaluationOutput } from '@/common/api/models/flatTaskEvaluationOutput';
import type { OralExamTaskEvaluationOutput } from '@/common/api/models/oralExamTaskEvaluationOutput';
import type { SubmissionEvaluation } from '@/common/api/models/submissionEvaluation';

export function isFlatTaskEvaluation(
  evaluation: SubmissionEvaluation | undefined | null,
): evaluation is FlatTaskEvaluationOutput {
  if (!evaluation || typeof evaluation !== 'object') {
    return false;
  }
  if ('kind' in evaluation && evaluation.kind === 'flat') {
    return true;
  }
  // kind is optional on the wire; a dimensions dict is still a flat evaluation.
  return 'dimensions' in evaluation && !('subEvaluations' in evaluation);
}

export function isOralExamTaskEvaluation(
  evaluation: SubmissionEvaluation | undefined | null,
): evaluation is OralExamTaskEvaluationOutput {
  if (!evaluation || typeof evaluation !== 'object') {
    return false;
  }
  if ('kind' in evaluation && evaluation.kind === 'oral_exam') {
    return true;
  }
  return 'subEvaluations' in evaluation;
}

function getLegacyFlatDimensions(
  evaluation: SubmissionEvaluation,
): Record<string, EvalDimRating> | null {
  if (isOralExamTaskEvaluation(evaluation) || isFlatTaskEvaluation(evaluation)) {
    return null;
  }
  const isLegacy = Object.values(evaluation).every(
    (value) => value && typeof value === 'object' && 'level' in value,
  );
  return isLegacy ? (evaluation as Record<string, EvalDimRating>) : null;
}

export function getFlatEvaluationDimensions(
  evaluation: SubmissionEvaluation | undefined | null,
): Record<string, EvalDimRating> {
  if (!evaluation) {
    return {};
  }
  if (isOralExamTaskEvaluation(evaluation)) {
    return {};
  }
  if (isFlatTaskEvaluation(evaluation)) {
    return evaluation.dimensions ?? {};
  }
  return getLegacyFlatDimensions(evaluation) ?? {};
}

/**
 * Pull non-empty per-dimension comments (autograder justifications) out of a
 * dim-rating map. Used by the Evaluation Desk sidebar — comments are display-only
 * and often never registered with RHF, so callers must not rely on useWatch
 * defaultValue to surface them.
 */
export function extractDimensionComments(
  dimensions: Record<string, { comment?: string | null } | undefined> | null | undefined,
): Record<string, string> | null {
  if (!dimensions) {
    return null;
  }
  const comments: Record<string, string> = {};
  Object.entries(dimensions).forEach(([dim, data]) => {
    if (data?.comment && typeof data.comment === 'string' && data.comment.length > 0) {
      comments[dim] = data.comment;
    }
  });
  return Object.keys(comments).length > 0 ? comments : null;
}
